/**
 * ProposalAnalyticsModal — drop-in modal for the RD dashboard showing
 * every open of a proposal link with IP, location, device, browser and
 * time-on-page. Triggered from the "Stats" button on each proposal card.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  X, Eye, Users, Clock, MapPin, Smartphone, Globe2, Loader2, RefreshCw,
} from "lucide-react";
import "./ProposalAnalyticsModal.css";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTs = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
};

const fmtRelative = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const fmtSeconds = (s) => {
  if (!s) return "0s";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const FLAG = (cc) => {
  if (!cc || cc.length !== 2) return "";
  const A = 0x1F1E6 - 65;
  return String.fromCodePoint(cc.toUpperCase().charCodeAt(0) + A) +
         String.fromCodePoint(cc.toUpperCase().charCodeAt(1) + A);
};

export default function ProposalAnalyticsModal({ proposalId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/proposals/${proposalId}/analytics`);
      setData(r.data);
    } catch (e) {
      console.error(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [proposalId]);

  return (
    <div className="pam-overlay" onClick={onClose} data-testid="pam-overlay">
      <div className="pam-modal" onClick={(e) => e.stopPropagation()} data-testid="pam-modal">
        <header className="pam-head">
          <div>
            <h2 data-testid="pam-title">
              {data?.agency_name ? `${data.agency_name} — link analytics` : "Link analytics"}
            </h2>
            <p>Every open of <code>/proposal/{data?.slug || "…"}</code> with IP, location, device and time on page.</p>
          </div>
          <div className="pam-head-actions">
            <button className="pam-icon" onClick={load} title="Refresh" data-testid="pam-refresh">
              <RefreshCw size={15} />
            </button>
            <button className="pam-icon" onClick={onClose} title="Close" data-testid="pam-close">
              <X size={16} />
            </button>
          </div>
        </header>

        <div className="pam-body" data-testid="pam-body">
        {loading ? (
          <div className="pam-loading"><Loader2 size={22} className="pam-spin" /></div>
        ) : !data ? (
          <div className="pam-empty">Could not load analytics for this proposal.</div>
        ) : (
          <>
            {/* KPI strip */}
            <div className="pam-kpis">
              <div className="pam-kpi">
                <Eye size={16} />
                <div>
                  <strong data-testid="pam-total-views">{data.total_views}</strong>
                  <span>Total opens</span>
                </div>
              </div>
              <div className="pam-kpi">
                <Users size={16} />
                <div>
                  <strong data-testid="pam-unique-visitors">{data.unique_visitors}</strong>
                  <span>Unique visitors</span>
                </div>
              </div>
              <div className="pam-kpi">
                <Clock size={16} />
                <div>
                  <strong>{data.engaged_views}</strong>
                  <span>Engaged (≥20s)</span>
                </div>
              </div>
              <div className="pam-kpi">
                <Clock size={16} />
                <div>
                  <strong>{fmtSeconds(data.total_time_seconds)}</strong>
                  <span>Total time spent</span>
                </div>
              </div>
            </div>

            {data.total_views === 0 ? (
              <div className="pam-noviews">
                <h3>No opens yet</h3>
                <p>
                  Once your prospect clicks the link, you&apos;ll see every visit here with
                  their location, device and how long they spent on the page.
                </p>
              </div>
            ) : (
              <>
                {/* Breakdown chips */}
                <div className="pam-breakdowns">
                  <Breakdown title="Country" Icon={Globe2} items={data.by_country} />
                  <Breakdown title="City"    Icon={MapPin}  items={data.by_city} />
                  <Breakdown title="Device"  Icon={Smartphone} items={data.by_device} />
                  <Breakdown title="Browser" Icon={Globe2} items={data.by_browser} />
                </div>

                {/* Timeline */}
                <section className="pam-timeline">
                  <h3>Full timeline</h3>
                  <div className="pam-events" data-testid="pam-events">
                    {data.events.map((e, i) => (
                      <div key={(e.id || "legacy") + i} className="pam-event">
                        <div className="pam-event-time">
                          <strong>{fmtTs(e.ts)}</strong>
                          <span>{fmtRelative(e.ts)}</span>
                        </div>
                        <div className="pam-event-loc">
                          <div className="pam-event-place">
                            {e.country_code && <span className="pam-flag">{FLAG(e.country_code)}</span>}
                            {e.city ? <strong>{e.city}</strong> : <strong>Unknown location</strong>}
                            {e.region && <span>{e.region}</span>}
                            {e.country && <span>{e.country}</span>}
                          </div>
                          {e.isp && <span className="pam-event-isp">{e.isp}</span>}
                        </div>
                        <div className="pam-event-tech">
                          <div>{e.device || "—"} · {e.os || "—"}</div>
                          <div>{e.browser || "—"}</div>
                        </div>
                        <div className="pam-event-ip">
                          <code>{e.ip || "—"}</code>
                          {e.visitor_id && <small title={e.visitor_id}>visitor {e.visitor_id.slice(0, 8)}</small>}
                        </div>
                        <div className="pam-event-time-on-page">
                          <Clock size={12} />
                          <strong>{fmtSeconds(e.time_on_page_seconds)}</strong>
                          <span>on page</span>
                        </div>
                        {e.legacy && <div className="pam-event-legacy">legacy</div>}
                      </div>
                    ))}
                  </div>
                </section>
              </>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
}

function Breakdown({ title, Icon, items }) {
  if (!items || items.length === 0) return null;
  const top = items.slice(0, 5);
  return (
    <div className="pam-breakdown">
      <div className="pam-breakdown-head"><Icon size={13} /> {title}</div>
      <ul>
        {top.map((it) => (
          <li key={it.label}>
            <span>{it.label}</span>
            <strong>{it.count}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}
